import type * as React from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { TableHead } from "@/components/ui/table";
import { cn } from "@/lib/utils";

export interface SortableTableHeadProps<T extends string = string>
  extends Omit<React.ComponentProps<"th">, "children"> {
  col: T;
  label: string;
  activeCol?: T;
  dir?: "ascending" | "descending";
  onSort: (col: T) => void;
  align?: "left" | "right" | "center";
}

export function SortableTableHead<T extends string = string>({
  col,
  label,
  activeCol,
  dir = "descending",
  onSort,
  align = "left",
  className,
  ...props
}: SortableTableHeadProps<T>) {
  const active = activeCol === col;
  const Icon = !active
    ? ArrowUpDown
    : dir === "ascending"
      ? ArrowUp
      : ArrowDown;

  return (
    <TableHead
      aria-sort={active ? dir : "none"}
      className={cn("px-3", align === "right" && "text-right", className)}
      {...props}
    >
      <button
        type="button"
        className={cn(
          "-mx-1 inline-flex min-h-6 items-center gap-1 rounded-sm px-1 font-medium hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring cursor-pointer",
          align === "right" && "ml-auto justify-end",
          active
            ? "text-foreground font-semibold"
            : "text-muted-foreground hover:text-foreground",
        )}
        onClick={() => onSort(col)}
      >
        <span>{label}</span>
        <Icon className="size-3.5 text-muted-foreground" aria-hidden="true" />
      </button>
    </TableHead>
  );
}
